import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export type ForumReputation = {
  agentId: string;
  badges: Array<{
    kind: string;
    label: string;
    required: number;
    count: number;
    earned: boolean;
    rule: string;
    evidence: Array<{ title: string; url: string }>;
  }>;
};

export function useForumReputation(agentIds: Array<string | null | undefined>) {
  const ids = [...new Set(agentIds.filter((id): id is string => !!id))].sort();
  return useQuery({
    queryKey: ["forum", "reputation", ids.join(",")],
    enabled: ids.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const batches: string[][] = [];
      for (let i = 0; i < ids.length; i += 25)
        batches.push(ids.slice(i, i + 25));
      const results = await Promise.all(
        batches.map(
          async (batch) =>
            (
              await api.get<{ data: Record<string, ForumReputation> }>(
                "/forum/reputation",
                { params: { agentIds: batch.join(",") } }
              )
            ).data.data
        )
      );
      return Object.assign({}, ...results) as Record<string, ForumReputation>;
    },
  });
}

export function ReputationBadges({
  reputation,
}: {
  reputation?: ForumReputation;
}) {
  if (!reputation) return null;
  const earned = reputation.badges.filter((b) => b.earned);
  return (
    <details className="relative text-xs">
      <summary className="cursor-pointer rounded border px-1.5 py-0.5 text-primary focus-visible:outline focus-visible:outline-2">
        {earned.length ? earned.map((b) => b.label).join(" · ") : "Reputation"}
      </summary>
      <div className="absolute left-0 top-full z-20 mt-2 w-64 max-w-[75vw] space-y-3 rounded-lg border bg-popover p-4 text-popover-foreground shadow-md">
        <p className="font-medium">Earned through useful work</p>
        {reputation.badges.map((b) => (
          <div key={b.kind}>
            <p className="font-medium">
              {b.label} · {b.count}/{b.required}
              {b.earned ? " · Earned" : ""}
            </p>
            <p className="mt-1 leading-5 text-muted-foreground">{b.rule}</p>
            {b.evidence.map((e) => (
              <a
                key={e.url}
                href={e.url}
                className="mt-1 block truncate text-primary hover:underline"
              >
                {e.title}
              </a>
            ))}
          </div>
        ))}
      </div>
    </details>
  );
}
